import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { apiError, formatWallTime, type RequestSwapInput, type ScheduledShift } from './schedulingLib';

/**
 * Ask to swap one shift — `POST /scheduling/me/swaps`.
 *
 * `SchedulingController.requestSwap`'s own comment: asking and approving are
 * separate keys (`scheduling.swap_request` / `scheduling.swap_approve`) so
 * that someone who could do both for their own shift would make the
 * manager's signature decorative. This dialog only ever calls the request
 * route, and — see `schedulingLib.ts`'s note on `RequestSwapInput` — it does
 * not pretend to track the request afterwards: there is no read endpoint for
 * a member of staff's own swaps, so the only honest thing to show is what
 * was actually sent.
 */
export default function RequestSwapDialog({
  shift,
  onClose,
  onSent,
}: {
  shift: ScheduledShift;
  onClose: () => void;
  onSent: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const input: RequestSwapInput = { shiftId: shift.id, reason: reason.trim() || undefined };
      await api.post('/scheduling/me/swaps', input);
      toast.success(t('scheduling.mine.swapSent'));
      onSent();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.mine.swapFailed')));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('scheduling.mine.requestSwapTitle')}
      description={t('scheduling.mine.requestSwapHint')}
      className="max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void send()} disabled={sending}>
            {t('scheduling.mine.send')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border p-3 text-sm">
          <p className="font-medium tabular-nums">{shift.businessDate}</p>
          <p className="text-ink-muted tabular-nums">
            {formatWallTime(shift.startsAt, shift.timezone, i18n.language)}–
            {formatWallTime(shift.endsAt, shift.timezone, i18n.language)}
          </p>
        </div>

        <Field label={t('scheduling.mine.swapReason')} hint={t('scheduling.mine.swapReasonHint')}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>

        <p className="text-xs text-ink-muted">{t('scheduling.mine.swapOpenNote')}</p>
      </div>
    </Dialog>
  );
}
